<template>
  <ChartCard
    title="Top Keywords"
    :skeleton="loading"
    :title-tooltip="titleTooltip"
    :see-more-button-text="seeMoreText"
    class="!min-h-[25.125rem]"
    :show-see-more="showSeeMore"
    @see-more="seeMoreClicked"
  >
    <template #filters>
      <slot name="topicChangeButton"></slot>
    </template>
    <div>
      <HorizontalBarChart
        :meters="slicedMeters"
        show-values
        :loading="loading"
        :no-data="noTopKeywordsData"
        :no-data-message="NO_KEYWORDS_FOUND"
        :max="meterBarUpperLimit"
        @meter-group-clicked="handleMeterGroupClicked"
      >
        <template #tooltip="{ meter, titleCallback }">
          {{ titleCallback(meter.label) }}
          <MetricList :metric-items="filterTooltipMetrics(meter.data, meter.tooltipData)" />
          <span v-if="!isComparisonPage" class="dash-text-base text-subtle"
            >Click a keyword to update page filters</span
          >
        </template>
      </HorizontalBarChart>

      <Select
        v-if="selectedMeter"
        v-model:shown="isSelectOpen"
        :dropdown="dropdownConfig"
        :options="keywordDropdownOptions"
        @update:model-value="handleKeywordSelected"
      />
    </div>
    <ChartLegend v-model="legendModel" :options="chartLegendOptions" />
  </ChartCard>
</template>

<script setup>
import { ChartCard } from '@dashhudson/dashing-ui/chart-card';
import { ChartLegend } from '@dashhudson/dashing-ui/chart-legend';
import { HorizontalBarChart } from '@dashhudson/dashing-ui/horizontal-bar-chart';
import { MetricList } from '@dashhudson/dashing-ui/metric-list';
import { Select } from '@dashhudson/dashing-ui/select';
import cloneDeep from 'lodash/cloneDeep';
import isEmpty from 'lodash/isEmpty';
import { computed, nextTick, ref } from 'vue';
import { useRoute } from 'vue-router';

import { useSocialListening } from '@/app/socialListening/composables/useSocialListening';
import {
  DEFAULT_OPERATOR_BY_GROUP_TYPE,
  KEYWORD_GROUP_TYPE,
  NO_KEYWORDS_FOUND,
  SOCIAL_LISTENING_REPORT_INTERACTION_TYPES,
  SOCIAL_LISTENING_REPORT_SECTIONS,
  SOCIAL_LISTENING_TOPIC_TOOLTIPS_GDI,
  TOPIC_COMPARISONS_TOOLTIPS,
  TRENDING_KEYWORDS_COUNT,
} from '@/app/socialListening/constants';
import {
  addGroup,
  createOrUpdateExcludeGroup,
} from '@/app/socialListening/utils/keyword-composer-utils';
import { socialListeningReportInteractionEvent } from '@/app/socialListening/utils/mixpanel-events';
import { parseToQuery } from '@/app/socialListening/utils/parsing';
import {
  convertTopKeywordsToKeywordAndHashtagMeters,
  filterTooltipMetrics,
} from '@/app/socialListening/utils/top-keywords';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';
import { useTrackingStore } from '@/stores/tracking';

const chartLegendOptions = [
  { label: 'Keyword', value: 'keywords' },
  { label: 'Hashtag', value: 'hashtags' },
];

const route = useRoute();

// stores
const trackingStore = useTrackingStore();
const listeningStore = useSocialListeningStore();
const topicsStore = useSocialListeningTopicsStore();
const { isComparisonPage } = useSocialListening();

// refs
const referenceNode = ref(null);
const selectedMeter = ref(null);
const isSelectOpen = ref(false);
const expanded = ref(false);
const legendModel = ref(['keywords', 'hashtags']);

// computed
const isReport = computed(() => trackingStore.currentRoute?.meta?.report);

const loading = computed(() => listeningStore.pending.topKeywords);
const dropdownConfig = computed(() => ({
  referenceNode: referenceNode.value,
  placement: 'bottom',
}));

const titleTooltip = computed(() => {
  if (isReport.value) {
    return null;
  }
  if (isComparisonPage.value) {
    return TOPIC_COMPARISONS_TOOLTIPS.TOP_KEYWORDS;
  }
  return SOCIAL_LISTENING_TOPIC_TOOLTIPS_GDI.TRENDING_KEYWORD;
});
const seeMoreText = computed(() => (expanded.value ? 'Collapse' : 'Expand'));
const topKeywordsMax = computed(() =>
  expanded.value ? TRENDING_KEYWORDS_COUNT.EXPANDED : TRENDING_KEYWORDS_COUNT.COLLAPSED,
);

const meters = computed(() =>
  convertTopKeywordsToKeywordAndHashtagMeters(
    listeningStore.topKeywords,
    legendModel.value,
    listeningStore.pending.topKeywords,
  ),
);

const slicedMeters = computed(() => meters.value.slice(0, topKeywordsMax.value));
const showSeeMore = computed(
  () =>
    !isReport.value &&
    !loading.value &&
    !isEmpty(slicedMeters.value) &&
    meters.value.length > TRENDING_KEYWORDS_COUNT.COLLAPSED,
);

const noTopKeywordsData = computed(() => meters.value.length === 0);

const meterMaxValue = computed(
  // Sort meters by total mentions and get the highest mention count
  () =>
    [...slicedMeters.value].sort((a, b) => b.tooltipData[0].value - a.tooltipData[0].value)[0]
      .tooltipData[0].value,
);

const meterBarUpperLimit = computed(() =>
  isEmpty(slicedMeters.value) ? 100 : meterMaxValue.value,
);

const KEYWORD_ACTIONS = {
  include: {
    rule: KEYWORD_GROUP_TYPE.INCLUDES,
    action: addGroup,
    getParams: () => [
      cloneDeep(listeningStore.keywordGroups) ?? [],
      KEYWORD_GROUP_TYPE.INCLUDES,
      DEFAULT_OPERATOR_BY_GROUP_TYPE[KEYWORD_GROUP_TYPE.INCLUDES],
      { keywords: [selectedMeter.value?.label].filter(Boolean) },
    ],
  },
  exclude: {
    rule: KEYWORD_GROUP_TYPE.EXCLUDES,
    action: createOrUpdateExcludeGroup,
    getParams: () => [
      cloneDeep(listeningStore.keywordGroups) ?? [],
      KEYWORD_GROUP_TYPE.EXCLUDES,
      { keywords: [selectedMeter.value?.label].filter(Boolean) },
    ],
  },
};

const keywordDropdownOptions = [
  { value: 'include', label: 'Include' },
  { value: 'exclude', label: 'Exclude' },
];

function handleKeywordSelected(event) {
  listeningStore.clearUnsavedTopicKeywordFilter();
  topicsStore.clearTempFilters();
  const mapping = KEYWORD_ACTIONS[event];
  if (!mapping) return;

  const { rule, action, getParams } = mapping;
  const params = getParams();
  const unsavedKeywordGroups = action(...params);

  listeningStore.updateKeywordFilter({ rule, word: selectedMeter.value.label });
  topicsStore.setTempFilters({ keywordsAndHashtags: parseToQuery(unsavedKeywordGroups) });
}

// methods
function handleMeterGroupClicked({ meter }) {
  // TODO: [SC-159009] In the future Include / Exclude Keywords will be replaced with
  //  showing the discovery about Top Keyword(Similar as it is currently in the Trends Page).
  //  Implement it for both - Topic & Comparison Page.
  if (isComparisonPage.value) {
    return;
  }

  socialListeningReportInteractionEvent({
    reportType: SOCIAL_LISTENING_REPORT_SECTIONS.TOP_KEYWORDS,
    interactionType: SOCIAL_LISTENING_REPORT_INTERACTION_TYPES.KEYWORD_CLICKED,
    path: route.path,
    reportValue: meter.data,
  });
  referenceNode.value = `#${meter.id}`;
  selectedMeter.value = meter;
  nextTick(() => {
    isSelectOpen.value = true;
  });
}

function seeMoreClicked() {
  expanded.value = !expanded.value;
}
</script>
